import type { MachineEtatOptions, Runnable, Transition, TransitionOut } from '@/features/domaine/machine-etats'
import type {
  AjouteFraisLaunch,
  AjouteFraisResult,
  AjouteFraisSelectionnables,
  AjouteFraisSelectionnés,
  AjouteFraisTerminé,
  TransitionSelectionnerFraisIn,
} from '@/features/interface/demande/dr/ajouter-frais/types.ts'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat.ts'
import { fusionneEtats } from '@/features/interface/machine-etats/tools.ts'
import type { DispositifDR } from '@/features/domaine/types/opco/Demande/DR/DispositifDR.ts'
import { tryLocationToParams } from '@/features/application/locations/locationToParams.ts'
import type { NavigateToDemandeExtranet } from '@/router/navigateTo'
import { ModelDemandeFrais } from '@/features/domaine/types/opco/Demande/shared/ModelDemandeFrais.ts'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/DR'
import ChoixNaturesFrais from '@/features/interface/demande/dr/ajouter-frais/cartes/ChoixNaturesFrais.vue'
import type { NatureFrais } from '@/features/domaine/types/opco'
import InformationMessage from '@/features/interface/demande/ajouter-drf/cartes/InformationMessage.vue'

function getNatureFraisParTypes(dispositif: DispositifDR, sousDispositif?: string) {
  const modelDemandeDossier = tryLocationToParams({
    to: 'detail',
    module: 'demande',
    domaine: 'formations',
    dispositif: dispositif as string,
    sousDispositif,
  } as NavigateToDemandeExtranet)?.model

  const fraisDispositif = modelDemandeDossier ? ModelDemandeFrais[modelDemandeDossier] : {
    Pedagogique: [],
    Annexe: []
  }

  const fraisDR = ModelDemandeFrais['MODEL_DR']

  return {
    Pedagogique: [...fraisDispositif!.Pedagogique, ...fraisDR.Pedagogique],
    Annexe: [...fraisDispositif!.Annexe, ...fraisDR.Annexe],
  }

}
function getFraisPossibles(launchParams: AjouteFraisLaunch) {
  const typeNatures = getNatureFraisParTypes(launchParams.dispositif, launchParams.sousDispositif)
  const naturesFrais = [...typeNatures.Pedagogique, ...typeNatures.Annexe]
  return naturesFrais
    .flatMap((nature) =>
      launchParams.stagiaires!.map((stagiaire) => ({
        idSalarie: stagiaire.idIndividuAdhEtablissement!,
        natureFrais: nature,
        idModule: launchParams.idModule,
        // PRT-1292 - TVA sur rémunération identique autres frais
        // tauxTva: nature === 'Remuneration' ? 0 : launchParams.tauxTVA,
        tauxTva: launchParams.tauxTVA,
        estSupprimable: true,
      })),
    )
    .filter((nouveauFrais) => {
      return !launchParams.frais.some(
        (fraisExistant) =>
          fraisExistant.idSalarie === nouveauFrais.idSalarie &&
          fraisExistant.natureFrais === nouveauFrais.natureFrais &&
          fraisExistant.idModule === nouveauFrais.idModule,
      )
    }) as FraisDTO[]
}

const ETAT_FRAIS_POSSIBLES_CALCULES = 'ETAT_FRAIS_POSSIBLES_CALCULES' as const
const transitionCalculerFraisPossibles: Runnable<AjouteFraisLaunch, AjouteFraisSelectionnables> = {
  run: (options) => {
    const fraisPossibles = getFraisPossibles(options.modelIn)
    const naturesFraisPossibles = [...new Set(fraisPossibles.map(frais => frais.natureFrais))]
    return {
      out: { fraisPossibles, naturesFraisPossibles },
      type: 'automatique',
    } as TransitionOut<AjouteFraisSelectionnables>
  }
 }

const ETAT_FRAIS_POSSIBLES_SELECTIONNES = 'ETAT_FRAIS_POSSIBLES_SELECTIONNES' as const
const transitionSelectionnerFrais: Runnable<TransitionSelectionnerFraisIn, AjouteFraisSelectionnés> = {
  run: async (options) => {
    if (options.modelIn.naturesFrais.length < 2) {
      return {
        out: { natureFraisACréer: options.modelIn.naturesFrais },
        type: 'automatique',
      } as TransitionOut<AjouteFraisSelectionnés>
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixNaturesFrais, ...options })

    return {
      out: { natureFraisACréer: result.value },
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<AjouteFraisSelectionnés>
  }
}

const ETAT_AJOUT_FRAIS_TERMINE = 'ETAT_AJOUT_FRAIS_TERMINE' as const
const transitionTerminerAjoutFrais: Runnable<{ natureFraisACréer: NatureFrais[], fraisPossibles: FraisDTO[] }, AjouteFraisTerminé> = {
  run: (options) => {
    const frais = options.modelIn.fraisPossibles?.filter(f =>
      options.modelIn.natureFraisACréer.includes(f.natureFrais as NatureFrais)
    )
    return {
      out: { frais },
      type: 'automatique',
    } as TransitionOut<AjouteFraisTerminé>
  }
}

const ETAT_AUCUN_FRAIS_POSSIBLE = 'ETAT_AUCUN_FRAIS_POSSIBLE' as const
const transitionAfficherAucunFraisPossible: Runnable<string, never> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    await display.runComponent({ component: InformationMessage, ...options })

    return {
      type: 'automatique',
    } as TransitionOut<never>
  }
}

export const AjouteFraisConfiguration: MachineEtatOptions<AjouteFraisLaunch, AjouteFraisResult> = {
  transitions: [
    {
      etatDepart: null,
      etatArrive: ETAT_FRAIS_POSSIBLES_CALCULES,
      runnable: transitionCalculerFraisPossibles,
      queryIn: (options) => options.launchParams,
    },
    {
      etatDepart: ETAT_FRAIS_POSSIBLES_CALCULES,
      etatArrive: ETAT_FRAIS_POSSIBLES_SELECTIONNES,
      runnable: transitionSelectionnerFrais,
      queryIn: (options) => ({
        naturesFrais: fusionneEtats<AjouteFraisResult>(options.etats).naturesFraisPossibles,
        lovFrais: options.launchParams.lovNatureFrais,
      }),
      if: (options) =>
        fusionneEtats<AjouteFraisResult>(options.etats).naturesFraisPossibles.length > 0,
    } as Transition<TransitionSelectionnerFraisIn, AjouteFraisSelectionnés>,
    {
      etatDepart: ETAT_FRAIS_POSSIBLES_CALCULES,
      etatArrive: ETAT_AUCUN_FRAIS_POSSIBLE,
      runnable: transitionAfficherAucunFraisPossible,
      queryIn: () => "Tous les frais ont déjà été ajouté !",
      if: (options) =>
        fusionneEtats<AjouteFraisResult>(options.etats).naturesFraisPossibles.length === 0,
    } as Transition<string, never>,
    {
      etatDepart: ETAT_FRAIS_POSSIBLES_SELECTIONNES,
      etatArrive: ETAT_AJOUT_FRAIS_TERMINE,
      runnable: transitionTerminerAjoutFrais,
      queryIn: (options) =>
        fusionneEtats<AjouteFraisResult>(options.etats)
    }
  ],
  resultCallback: options => fusionneEtats<AjouteFraisResult>(options.etats),
  disposeCallback: () => useMachineEtatDisplay().hide(),
}
